'use client';

import React, { useState } from 'react';
import { ArrowLeft2 } from 'iconsax-react';
import { LayoutProps } from '@/types/generalSettings';

// IMPORT THE NEW HELPER
import { getIconByString } from '@/components/layout/MenuIconHelper';
import ExchangeMenuItemSimple from '@/components/layout/desktop/megamenu/ExchangeMenuItemSimple';
import SectionHeader from '@/components/layout/desktop/SectionHeader';
// import ExchangeMenuItemSimpleSkeleton from '@/components/layout/desktop/megamenu/ExchangeMenuItemSimpleSkeleton';

interface ExtendedMenuItem extends LayoutProps.MenuItem {
    newMenuIconBox?: {
        menuIcon?: string | null;
        postTypes?: string[] | null;
    };
}

interface MegaMenuClientProps {
    tabs: ExtendedMenuItem[];
    content: any;
    brokerAd?: LayoutProps.BrokersAd[];
    exchangeForeignAd?: LayoutProps.BrokersAd[];
    exchangeIranianAd?: LayoutProps.BrokersAd[];
    propAd?: LayoutProps.BrokersAd[];
}

// --- TRANSFORMER ---
const transformToMenuItem = (
    node: any,
    type: 'broker' | 'exchange' | 'prop' | 'gold-platforms',
): any => {
    return {
        path: node.uri,
        label: node.title,
        connectedObject: {
            contentTypeName: type,
            brokerSingle: node.brokerSingle,
            ...(type === 'broker' ? { brokerFields: node.brokerFields } : {}),
            ...(type === 'exchange' ? { exchangeFields: node.exchangeFields } : {}),
            ...(type === 'prop' ? { exchangeFields: node.exchangeFields } : {}),
            ...(type === 'gold-platforms' ? { goldFields: node.goldFields } : {}),
            postRatings: node.postRatings,
        },
    };
};

const MegaMenuClient: React.FC<MegaMenuClientProps> = ({
    tabs,
    content,
    brokerAd,
    exchangeForeignAd,
    exchangeIranianAd,
    propAd,
}) => {
    const [activeTabId, setActiveTabId] = useState<number | string>(tabs[0]?.databaseId || 0);
    const activeTab = tabs.find((t) => t.databaseId === activeTabId) || tabs[0];

    const getPostTypeFromTab = (label: string) => {
        if (!label) return 'unknown';
        if (label.includes('صرافی')) return 'exchange';
        if (label.includes('بروکر')) return 'broker';
        if (label.includes('پراپ')) return 'prop';
        if (label.includes('طلا')) return 'gold-platforms';
        return 'unknown';
    };

    const postType = getPostTypeFromTab(activeTab.label || '');

    // --- HELPER: Find Sub-Link Info ---
    const getLinkInfo = (
        uriPart: string,
        fallbackLabel: string,
        fallbackUri: string,
        excludePart?: string,
    ) => {
        const subLinks = activeTab.childItems?.edges.map((e) => e.node) || [];
        const found = subLinks.find((link) => {
            const matchesPart = link?.uri?.includes(uriPart);
            const matchesExclude = excludePart ? link?.uri?.includes(excludePart) : false;
            return matchesPart && !matchesExclude;
        });

        return {
            label: found?.label || fallbackLabel,
            uri: found?.uri || fallbackUri,
        };
    };

    // --- RENDER CONTENT ---
    const renderContent = () => {
        // 1. EXCHANGE: Split View
        if (postType === 'exchange') {
            const iranianAdIds = (exchangeIranianAd || []).map((ad) => ad.databaseId);
            const foreignAdIds = (exchangeForeignAd || []).map((ad) => ad.databaseId);

            const filteredIranianNodes = (content?.iranianExchanges?.nodes || []).filter(
                (node: any) => !iranianAdIds.includes(node.databaseId),
            );
            const filteredForeignNodes = (content?.foreignExchanges?.nodes || []).filter(
                (node: any) => !foreignAdIds.includes(node.databaseId),
            );

            const iranianNodes = [...(exchangeIranianAd || []), ...filteredIranianNodes].slice(
                0,
                4,
            );
            const foreignNodes = [...(exchangeForeignAd || []), ...filteredForeignNodes].slice(
                0,
                4,
            );

            const iranianInfo = getLinkInfo(
                'best-iranian-crypto-exchanges',
                'بهترین صرافی‌ ایرانی',
                '/best-iranian-crypto-exchanges/',
            );

            const foreignInfo = getLinkInfo(
                'crypto-exchange',
                'بهترین صرافی‌ خارجی',
                '/crypto-exchange/',
                'iranian',
            );

            return (
                <div className='grid grid-cols-2 gap-x-2 min-w-[750px] w-full h-full items-start overflow-y-auto'>
                    {/* Iranian Section */}
                    <div className='grid relative'>
                        {iranianNodes.map((node: any, idx: number) => {
                            const isAd = idx < (exchangeIranianAd?.length || 0);
                            return (
                                <ExchangeMenuItemSimple
                                    key={idx}
                                    index={idx}
                                    childItem={transformToMenuItem(node, 'exchange')}
                                    menu={true}
                                    isAd={isAd}
                                    hasAd={(exchangeIranianAd?.length || 0) > 0}
                                    className='border border-primary/5 dark:border-white/5'
                                />
                            );
                        })}
                        <SectionHeader label={iranianInfo.label} uri={iranianInfo.uri} />
                    </div>

                    {/* Foreign Section */}
                    <div className='grid relative'>
                        {foreignNodes.map((node: any, idx: number) => {
                            const isAd = idx < (exchangeForeignAd?.length || 0);
                            return (
                                <ExchangeMenuItemSimple
                                    key={idx}
                                    index={idx}
                                    childItem={transformToMenuItem(node, 'exchange')}
                                    menu={true}
                                    isAd={isAd}
                                    hasAd={(exchangeForeignAd?.length || 0) > 0}
                                    className='border ps-3 border-primary/5 dark:border-white/5'
                                />
                            );
                        })}
                        <SectionHeader label={foreignInfo.label} uri={foreignInfo.uri} />
                    </div>
                </div>
            );
        }

        // 2. OTHERS: Standard Grid
        let nodes: any[] = [];
        let type: 'broker' | 'prop' | 'gold-platforms' | null = null;
        let linkInfo = { label: '', uri: '#' };
        let adNodes: any[] = [];

        if (postType === 'broker') {
            adNodes = brokerAd || [];
            const adIds = adNodes.map((ad) => ad.databaseId);
            const filteredNodes = (content?.brokers?.nodes || []).filter(
                (node: any) => !adIds.includes(node.databaseId),
            );
            nodes = [...adNodes, ...filteredNodes].slice(0, 4);
            type = 'broker';
            linkInfo = getLinkInfo('best-broker', 'بهترین بروکر', '/best-broker/');
        } else if (postType === 'prop') {
            adNodes = propAd || [];
            const adIds = adNodes.map((ad) => ad.databaseId);
            const filteredNodes = (content?.props?.nodes || []).filter(
                (node: any) => !adIds.includes(node.databaseId),
            );
            nodes = [...adNodes, ...filteredNodes].slice(0, 4);
            type = 'prop';
            linkInfo = getLinkInfo('prop', 'بهترین پراپ', '/prop/');
        } else if (postType === 'gold-platforms') {
            nodes = (content?.goldPlatforms?.nodes || []).slice(0, 4);
            type = 'gold-platforms';
            linkInfo = getLinkInfo('gold-platforms', ' سایت‌های‌ طلا', '/gold-platforms/');
        }

        if (nodes.length === 0 || !type) {
            return (
                <div className='flex items-center justify-center h-40 text-gray-400 text-sm'>
                    محتوایی یافت نشد
                </div>
            );
        }

        return (
            <div className='grid w-[370px] relative content-start overflow-y-auto'>
                {nodes.map((node: any, idx: number) => {
                    const isAd = idx < adNodes.length;
                    return (
                        <ExchangeMenuItemSimple
                            key={idx}
                            index={idx}
                            childItem={transformToMenuItem(node, type!)}
                            menu={true}
                            isAd={isAd}
                            hasAd={adNodes.length > 0}
                            className='border border-primary/5 dark:border-white/5'
                        />
                    );
                })}
                <SectionHeader type={postType} label={linkInfo.label} uri={linkInfo.uri} />
            </div>
        );
    };

    return (
        <div className='flex w-full h-full min-h-[250px]'>
            {/* RIGHT SIDE: TABS */}
            <div className='w-[260px] bg-gray-50 dark:bg-white/5 py-4 flex flex-col gap-1'>
                {tabs.map((tab) => {
                    const isActive = tab.databaseId === activeTabId;
                    const iconName = tab.newMenuIconBox?.menuIcon;

                    // Call the reusable helper
                    const icon = getIconByString(
                        iconName,
                        `size-5 ${isActive ? 'text-progress' : 'text-gray-400'}`,
                    );

                    return (
                        <div
                            key={tab.databaseId}
                            onMouseEnter={() => setActiveTabId(tab.databaseId || 0)}
                            className={`
                                group px-5 py-3 cursor-pointer flex items-center gap-3 transition-all relative
                                ${isActive ? 'bg-white dark:bg-primary-active shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-white/10'}
                            `}
                        >
                            {isActive && (
                                <div className='absolute right-0 top-0 bottom-0 w-1 bg-progress rounded-l-md' />
                            )}
                            {icon && <div className='shrink-0'>{icon}</div>}
                            <span
                                className={`text-sm flex-1 ${isActive ? 'font-bold text-black dark:text-white' : 'text-gray-500 font-medium'}`}
                            >
                                {tab.label}
                            </span>
                            {isActive && <ArrowLeft2 size={14} className='text-progress' />}
                        </div>
                    );
                })}
            </div>

            {/* LEFT SIDE: CONTENT */}
            <div className='flex-1 p-6 bg-white dark:bg-primary-active'>{renderContent()}</div>
        </div>
    );
};

export default MegaMenuClient;
